import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    Divider,
    ModalBody,
    SkeletonText,
    Box,
    AspectRatio,
    ModalFooter,
    Spacer,
    Button,
    Text,
    useToast,
    VStack,
    HStack,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { EmailType } from '@acp/shared';
import { useEffect } from 'react';
import { FieldErrors } from 'react-hook-form';
import { FaIcon } from '@acp/core-ui';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons';
import { getEmailPreview } from '../../features/email/service';
import { ForwardEmailFormValues } from '../../features/email';

export function SendEmailPreviewModal({
    isValid,
    formErrors,
    isOpen,
    onSubmit,
    onClose,
    formValues,
    metaData,
    isLoading,
}: {
    isValid: boolean;
    formErrors: FieldErrors<ForwardEmailFormValues>;
    isOpen: boolean;
    isLoading: boolean;
    onSubmit: () => void;
    onClose: () => void;
    formValues: ForwardEmailFormValues;
    metaData: {
        emailType: EmailType;
        noticeId?: number;
        quoteId?: number;
        invoiceId?: number;
        propertyId?: number;
    };
}) {
    const toast = useToast();

    const emailPreviewMutation = useMutation({
        mutationFn: async ({
            message, //
            toAddresses,
            ccAddresses,
            emailFooter,
        }: {
            message: string | null;
            toAddresses?: string[];
            ccAddresses?: string[];
            emailFooter?: string;
        }) => getEmailPreview(metaData.emailType, {
            ...metaData,
            message,
            toAddresses,
            ccAddresses,
            emailFooter,
        }),
        onError: (error) => {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            onPreviewClose();

            toast({
                title: 'Request Failed',
                description: error.message,
                status: 'error',
                isClosable: true,
            });
        },
    });

    const onPreviewClose = () => {
        emailPreviewMutation.reset();
        onClose();
    };

    useEffect(() => {
        if (isOpen && formValues != null) {
            emailPreviewMutation.mutate({
                message: formValues.emailMessage,
                toAddresses: formValues.toEmails.map((email) => email.value),
                ccAddresses: formValues.ccEmails.map((email) => email.value),
                emailFooter: formValues.emailFooter,
            });
        }
    }, [isOpen]);

    const getEmailAddresses = (type: 'to' | 'cc') => emailPreviewMutation.data?.recipients
        ?.filter((recipient) => recipient.type === type)
        ?.map((recipient) => recipient.email)
        .join(', ');

    return (
        <Modal isOpen={isOpen} onClose={onPreviewClose} size="3xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text>Preview email</Text>
                    <ModalCloseButton onClick={onPreviewClose} />
                </ModalHeader>
                <Divider />
                <ModalBody>
                    {emailPreviewMutation?.isPending ? (
                        <Box p={5}>
                            <SkeletonText noOfLines={3} spacing="3" skeletonHeight="5" />
                            <SkeletonText mt={10} noOfLines={10} spacing="3" skeletonHeight="5" />
                        </Box>
                    ) : (
                        emailPreviewMutation.error == null && (
                            <Box>
                                <Box p={5}>
                                    <Text>
                                        <strong>{emailPreviewMutation.data?.subject}</strong>
                                    </Text>
                                    <Text mt={1}>
                                        To:&nbsp;
                                        <strong>{getEmailAddresses('to')}</strong>
                                    </Text>
                                    {getEmailAddresses('cc') !== '' && (
                                        <Text mt={1}>
                                            CC:&nbsp;
                                            <strong>{getEmailAddresses('cc')}</strong>
                                        </Text>
                                    )}
                                    <Text mt={1}>
                                        From:&nbsp;
                                        <strong>{emailPreviewMutation.data?.from?.email}</strong>
                                    </Text>
                                </Box>
                                <Divider />
                                <AspectRatio mt={10} maxH="50vh">
                                    <iframe
                                        title="Email Preview"
                                        srcDoc={emailPreviewMutation.data?.preview_html}
                                    />
                                </AspectRatio>
                            </Box>
                        )
                    )}
                </ModalBody>
                <Divider />
                <ModalFooter>
                    <VStack w="100%">
                        <HStack w="100%">
                            <Button
                                size="md"
                                onClick={onPreviewClose}
                                colorScheme="muted"
                                variant="ghost"
                            >
                                Close
                            </Button>
                            <Spacer />
                            <Button
                                colorScheme="secondary"
                                size="md"
                                variant="solid"
                                onClick={onSubmit}
                                isDisabled={isValid === false || emailPreviewMutation?.isPending}
                                isLoading={isLoading}
                            >
                                <FaIcon icon={faPaperPlane} mr={2} />
                                <Text>Send</Text>
                            </Button>
                        </HStack>
                        <HStack w="100%">
                            <Spacer />
                            {emailPreviewMutation?.isPending === false
                                && Object.keys(formErrors).map((key) => (
                                    <Text
                                        key={`preview_error_${key}`}
                                        color="red.500"
                                        fontSize="sm"
                                    >
                                        {formErrors[key as keyof typeof formErrors]?.message}
                                    </Text>
                                ))}
                        </HStack>
                    </VStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
